import { isError, useMutation } from '@tanstack/react-query'
import { mutationKeys } from '../utils/mutationKeys'
import {
  confirmReservationConsultation,
  createAttendanceRecurrence,
  getRecurrencePossibility,
  requestImmediatePix,
  requestPixPayment,
  requestScheduleAttendancePix,
  validateTransaction,
} from '../checkout-services'
import { toast, ToastCompactProps } from '@conexasaude/conexa-hero'
import { ConexaGenericError } from '@/services/httpClient'
import {
  ConfirmReservationConsultationData,
  CreateAttendanceRecurrenceParams,
  IRequestPixPaymentParams,
} from '../types'
import axios from 'axios'
import { useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useConexaQuery } from '@/hooks/useConexaQuery'
import { checkoutQueryKeys } from '../utils/queryKeys'
import { GetRecurrencePossibilityParams } from './checkout.types'

export const useRequestPixPaymentMutation = () => {
  const cancelSourceRef = useRef<ReturnType<
    typeof axios.CancelToken.source
  > | null>(null)

  const { mutateAsync, isLoading, data } = useMutation({
    mutationFn: (params: IRequestPixPaymentParams) => {
      cancelSourceRef.current?.cancel('Request cancelled')
      const source = axios.CancelToken.source()
      cancelSourceRef.current = source

      return requestPixPayment(params, source.token)
    },
    mutationKey: mutationKeys.pixPaymentKey(),
    onSettled: () => {
      cancelSourceRef.current = null
    },
    onError: (error: ConexaGenericError) => {
      toast({
        type: 'Compact',
        variant: 'error',
        message: error.response.data.msg,
      })
    },
  })
  return {
    paymentData: data,
    requestPixPayment: mutateAsync,
    isRequestingPixPayment: isLoading,
    isRequestingError: isError,
    abortRequest: () => cancelSourceRef.current?.cancel('Request cancelled'),
  }
}

export function useRequestImmediateAttendancePixMutation() {
  const { mutateAsync, isLoading } = useMutation({
    mutationFn: requestImmediatePix,
    mutationKey: mutationKeys.requestImmediateAttendanceKey(),
    onError: (err: ConexaGenericError) => {
      const errorMessage =
        err?.response?.data?.msg || err?.response?.msg || err?.message

      toast({
        message: errorMessage,
        variant: 'error',
        autoClose: errorMessage?.length < 50,
        autoCloseDuration: errorMessage?.length < 50 ? 5000 : null,
      })
    },
  })

  return {
    requestImmediatePix: mutateAsync,
    isRequestingImmediatePix: isLoading,
  }
}

export function useRequestScheduleAttendancePixMutation(
  selectedCouncil?: string | number,
  selectedSpecialityId?: string | number,
) {
  const history = useHistory()

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: requestScheduleAttendancePix,
    mutationKey: mutationKeys.requestScheduleAttendanceKey(),

    onError: (err: ConexaGenericError) => {
      const errorMessage: string =
        err?.response?.data?.msg || err?.response?.msg || err?.message

      const isSlotNotAvailable = (errorMessage || '')
        ?.toLowerCase()
        ?.includes('encaixe')

      const toastAction: Partial<ToastCompactProps> = isSlotNotAvailable
        ? {
            actionLabel: 'Encontrar encaixe',
            action: () =>
              history.push(
                `/atendimento-agendado/especialidade/encaixe/:${selectedCouncil}/:${selectedSpecialityId}`,
              ),
          }
        : {}

      toast({
        variant: 'error',
        message: errorMessage,
        autoClose: errorMessage?.length < 50,
        autoCloseDuration: errorMessage?.length < 50 ? 5000 : null,
        ...toastAction,
      })

      history.push('/')
    },
  })

  return {
    requestScheduleAttendancePix: mutateAsync,
    isRequestingSchedulePix: isLoading,
  }
}

export function useValidateTransactionQuery(
  chargeId: string,
  refetchInterval: number,
) {
  const { data } = useConexaQuery({
    queryKey: checkoutQueryKeys.validateTransactionKey(chargeId),
    service: () => validateTransaction(chargeId),
    config: {
      refetchInterval: refetchInterval || false,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: true,
      enabled: !!chargeId,
    },
  })

  return {
    transaction: data,
  }
}

export function useGetRecurrencePossibility({
  attendanceId,
  enabled,
}: GetRecurrencePossibilityParams) {
  return useConexaQuery({
    queryKey: checkoutQueryKeys.getRecurrencePossibility(attendanceId),
    service: () => getRecurrencePossibility(attendanceId),
    config: {
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: true,
      enabled: enabled && !!attendanceId,
    },
  })
}

export function useCreateAttendanceRecurrenceMutation() {
  return useMutation({
    mutationFn: (data: CreateAttendanceRecurrenceParams) =>
      createAttendanceRecurrence(data),
    mutationKey: mutationKeys.createAttendanceRecurrenceKey(),
    onError: (err: ConexaGenericError) => {
      const errorMessage =
        err?.response?.data?.msg || err?.response?.msg || err?.message

      toast({
        variant: 'error',
        message: errorMessage,
      })
    },
  })
}

export function useConfirmReservationConsultationMutation() {
  return useMutation({
    mutationFn: (data: ConfirmReservationConsultationData) =>
      confirmReservationConsultation(data),
    mutationKey: mutationKeys.confirmReservationConsultationKey(),
    onError: (err: ConexaGenericError) => {
      const errorMessage =
        err?.response?.data?.msg || err?.response?.msg || err?.message

      toast({
        variant: 'error',
        message: errorMessage,
      })
    },
  })
}
